import { DEFAULT_BASEMAP, isBasemapChoice, type BasemapChoice } from '../map/basemaps'
import { isLayerId } from '../map/layers/registry'
import { read, readJson, remove, write } from '../storage'
import { loadPlaces } from './places.svelte'
import { loadPushPrefs } from './push.svelte'

/**
 * localStorage-backed preferences. `weather.lastLocation` keeps its Phase 0
 * name so a returning user's saved location survived the re-platform; the
 * panels moved to a new key in Phase 9, for the reason `loadPanels` gives.
 * Every access goes through `lib/storage.ts`, which is guarded.
 */

const KEY_LOCATION = 'weather.lastLocation'
/**
 * Where the phone last was, written only by a successful fix. Distinct from
 * `lastLocation`, which is the last place *looked at*: the placeholder shown
 * while locating must never be a place the phone is not known to have been.
 */
const KEY_FIX = 'weather.lastFix'
const KEY_PANELS = 'weather.panelState'
/**
 * Phase 0 to 8's panel key. Every panel that ever *mounted* open wrote `true`
 * to it, so only its `false`s are believed, once, and it is then removed.
 */
const KEY_PANELS_OLD = 'weather.panels'
const KEY_BASEMAP = 'weather.basemap'
const KEY_LAYERS = 'weather.layers'
const KEY_LAYER_OPACITY = 'weather.layerOpacity'
const KEY_SIDEBAR = 'weather.sidebar'
const KEY_MODEL_WINDOW = 'weather.modelWindow'

export interface StoredLocation {
  lat: number
  lon: number
  label?: string
  zip?: string
}

export interface StoredFix {
  lat: number
  lon: number
  /** When the fix was taken, epoch ms. */
  at: number
}

function validFix(v: unknown): v is StoredFix {
  return validLocation(v) && Number.isFinite((v as StoredFix).at)
}

function validLocation(v: unknown): v is StoredLocation {
  if (!v || typeof v !== 'object') return false
  const l = v as StoredLocation
  return Number.isFinite(l.lat) && Number.isFinite(l.lon)
}

export const prefs = $state({
  lastLocation: null as StoredLocation | null,
  lastFix: null as StoredFix | null,
  panels: {} as Record<string, boolean>,
  /** `auto` follows the theme; anything else is somebody's explicit choice. */
  basemap: DEFAULT_BASEMAP as BasemapChoice,
  /** Bottom-first overlay stack. Empty means "never chosen", not "all off". */
  layers: [] as string[],
  layerOpacity: {} as Record<string, number>,
  /**
   * Desktop only: false folds the section panel for a map beside the rail
   * (Phase 11 plan §7.1). The key is still `weather.sidebar`.
   */
  sidebarOpen: true,
  /**
   * How far ahead the model layers look: `6h` | `12h` | `48h` | `all`.
   *
   * A preference about how far ahead you look rather than about a particular
   * run, so unlike the run selection it survives a reload. Stored as a raw
   * string and validated by `initModels()` against the window list, so a
   * corrupt entry boots at the default rather than at nothing.
   */
  modelWindow: '' as string,
})

/** Read once at boot, before the first render, so nothing flashes. */
export function loadPrefs(): void {
  const loc = readJson<unknown>(KEY_LOCATION)
  prefs.lastLocation = validLocation(loc)
    ? { lat: loc.lat, lon: loc.lon, label: loc.label, zip: loc.zip }
    : null

  const fix = readJson<unknown>(KEY_FIX)
  prefs.lastFix = validFix(fix) ? { lat: fix.lat, lon: fix.lon, at: fix.at } : null

  prefs.panels = loadPanels()

  const basemap = read(KEY_BASEMAP)
  prefs.basemap = isBasemapChoice(basemap) ? basemap : DEFAULT_BASEMAP

  // A renamed or retired registry entry is dropped, not restored: the id is
  // the contract and stale storage must not resurrect a layer that is gone.
  const stored = readJson<unknown>(KEY_LAYERS)
  prefs.layers = Array.isArray(stored) ? [...new Set(stored.filter(isLayerId))] : []

  const opacity = readJson<unknown>(KEY_LAYER_OPACITY)
  prefs.layerOpacity =
    opacity && typeof opacity === 'object' && !Array.isArray(opacity)
      ? Object.fromEntries(
          Object.entries(opacity as Record<string, unknown>)
            .filter(([k, v]) => isLayerId(k) && typeof v === 'number' && v >= 0 && v <= 1)
            .map(([k, v]) => [k, v as number]),
        )
      : {}

  // Only an explicit "hide" is stored, so a corrupt or missing entry leaves the
  // forecast visible rather than booting into a map with no apparent sidebar.
  prefs.sidebarOpen = read(KEY_SIDEBAR) !== 'hidden'
  prefs.modelWindow = read(KEY_MODEL_WINDOW) ?? ''

  // Saved places and the default location, in the same pass: the boot reads
  // `openTo` before anything has rendered.
  loadPlaces()
  loadPushPrefs()
}

export function setModelWindow(window: string): void {
  prefs.modelWindow = window
  write(KEY_MODEL_WINDOW, window)
}

export function setSidebarOpen(open: boolean): void {
  prefs.sidebarOpen = open
  write(KEY_SIDEBAR, open ? 'shown' : 'hidden')
}

export function setLayers(ids: string[]): void {
  prefs.layers = ids
  write(KEY_LAYERS, JSON.stringify(ids))
}

export function setLayerOpacity(id: string, opacity: number): void {
  prefs.layerOpacity = { ...prefs.layerOpacity, [id]: opacity }
  write(KEY_LAYER_OPACITY, JSON.stringify(prefs.layerOpacity))
}

export function setLastLocation(loc: StoredLocation | null): void {
  prefs.lastLocation = loc
  if (loc) write(KEY_LOCATION, JSON.stringify(loc))
  else remove(KEY_LOCATION)
}

export function setLastFix(fix: StoredFix): void {
  prefs.lastFix = fix
  write(KEY_FIX, JSON.stringify(fix))
}

/**
 * Panels that no longer exist. A stored choice about one is dropped. Current,
 * Forecast, Hourly and Alerts became fixed sections of the sidebar and the
 * sheet in Phase 10 (plan §4.1): with them went the only panels that opened
 * by default, so every panel left starts collapsed.
 */
const RETIRED_PANELS: ReadonlySet<string> = new Set([
  'location',
  'about',
  'current',
  'forecast',
  'hourly',
  'alerts',
  // Stage 10.5: the table is the Meteogram's Table view, and Settings is a
  // screen of its own; neither is a panel any more.
  'compare-table',
  'settings',
])

/**
 * The stored open/closed choices, migrating the old key the first time.
 *
 * A `<details>` that mounts open fires `toggle` in Chromium and WebKit, and
 * `Panel` used to store every `toggle`, so every returning browser has `true`
 * stored for every panel it has ever shown. A `true` there is
 * indistinguishable from the mount writing it; a `false` can only have come
 * from somebody closing that panel. So the `false`s are carried across, the
 * `true`s are dropped, and the new key is what tells the next boot the
 * migration has happened.
 */
function loadPanels(): Record<string, boolean> {
  if (read(KEY_PANELS) !== null) return panelChoices(readJson(KEY_PANELS), () => true)
  if (read(KEY_PANELS_OLD) === null) return {}
  const kept = panelChoices(readJson(KEY_PANELS_OLD), (open) => !open)
  write(KEY_PANELS, JSON.stringify(kept))
  remove(KEY_PANELS_OLD)
  return kept
}

function panelChoices(raw: unknown, keep: (open: boolean) => boolean): Record<string, boolean> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>).filter(
      (entry): entry is [string, boolean] =>
        typeof entry[1] === 'boolean' &&
        keep(entry[1]) &&
        !RETIRED_PANELS.has(entry[0]),
    ),
  )
}

export function isPanelOpen(id: string): boolean {
  return prefs.panels[id] ?? false
}

/** Store a user's choice. `Panel` calls it only for a real change, never for a mount. */
export function setPanelOpen(id: string, open: boolean): void {
  prefs.panels[id] = open
  write(KEY_PANELS, JSON.stringify(prefs.panels))
}

export function setBasemap(id: BasemapChoice): void {
  prefs.basemap = id
  // `auto` is the default, so choosing it again is removing the choice.
  if (id === 'auto') remove(KEY_BASEMAP)
  else write(KEY_BASEMAP, id)
}
